import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useAmplitude } from 'react-amplitude-hooks'
import { faLightbulb } from '@fortawesome/pro-solid-svg-icons'

import { IMainState } from 'store/states'
import * as fromLessonActions from 'store/actions/lessons.action'
import { ILessonModel } from 'models/lesson.model'
import {
  AmplitudeEvents,
  AssignedLessonStateEnum,
  SynchronousCourseTypes,
} from 'enum'
import { lessonValidatorByRef } from 'helpers/validators.helper'
import * as fromChatActions from 'store/actions/chat.action'
import { IChatMessageModel } from 'models/chatMessage.model'
import { useCourse } from 'contexts'
import { useSearch } from 'hooks/react.hooks'
import { IInfoModal } from 'models/modals.model'

const modalDefaultValues: IInfoModal = {
  show: false,
  title: '',
  message: '',
  buttonMessage: '',
}

const useActions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const [origin] = useSearch<'origin'>()

  const { course, courseProgress, section } = useSelector(
    ({ courses }: IMainState) => courses
  )

  const normativeAccessReason = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons.normativeAccessReason
  )

  const nextLessonRejection = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons.nextLessonRejection
  )

  const identifier = useSelector(
    ({ user }: IMainState) => user.user?.identifier
  )

  const loading = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons.loading
  )

  const [submit, setSubmit] = useState(false)
  const [currentLesson, setCurrentLesson] = useState({} as any)
  const [modalData, setModalData] = useState<IInfoModal>(modalDefaultValues)
  const [showFinishModal, setShowFinishModal] = useState(false)
  const [pendingActivities, setPendingActivities] = useState(false)
  const mandatoriesRef = useRef<(HTMLElement | null)[]>([])

  const setMandatoriesRef = useCallback((array: (HTMLElement | null)[]) => {
    mandatoriesRef.current = array
  }, [])

  const { logEvent } = useAmplitude()

  const {
    multimediaProgress,
    canAnswer,
    mandatoryTaskUploadInProgress,
    validations: {
      lesson: {
        everyEvaluationsHasResponse,
        everyHasResponse,
        everyTasksHasResponse,
      },
    },
  } = useCourse()

  const handleGetPrevLesson = () => {
    const { lessons } = course
    if (currentLesson.order > 1) {
      const prevLesson = lessons.find(
        (lesson: ILessonModel) => lesson.order === currentLesson.order - 1
      )
      if (prevLesson) {
        dispatch({
          type: fromLessonActions.LESSONS_GET_LESSON,
          payload: prevLesson.id,
        })
        setCurrentLesson(prevLesson)
      }
    }
  }

  const showIncompleteMultimediaModal = useCallback(() => {
    setModalData({
      show: true,
      title: t('lessons.modals.incomplete.title'),
      message: t('lessons.modals.incomplete.multimedia'),
      icon: faLightbulb,
      iconType: 'info',
      buttonMessage: t('lessons.modals.buttons.accept'),
      onContinue: () => setModalData({ show: false }),
      onClose: () => setModalData({ show: false }),
    })
  }, [t])

  const showIncompleteEvaluationsModal = useCallback(() => {
    setModalData({
      show: true,
      title: t('lessons.modals.incomplete.title'),
      message: t('lessons.modals.incomplete.evaluation'),
      icon: faLightbulb,
      iconType: 'info',
      buttonMessage: t('lessons.modals.buttons.accept'),
      onContinue: () => setModalData({ show: false }),
      onClose: () => setModalData({ show: false }),
    })
  }, [t])

  const showIncompleteTasksModal = useCallback(() => {
    setModalData({
      show: true,
      title: t('lessons.modals.incomplete.title'),
      message: t('lessons.modals.incomplete.task'),
      icon: faLightbulb,
      iconType: 'info',
      buttonMessage: t('lessons.modals.buttons.accept'),
      onContinue: () => setModalData({ show: false }),
      onClose: () => setModalData({ show: false }),
    })
  }, [t])

  const prepareModalData = useCallback(
    (extraData: any = undefined) => {
      const message = (
        <div>
          {t('lessons.modals.incomplete.message_part1')}
          <b>{` ${extraData} ${t('lessons.modals.incomplete.lessons')} `}</b>
          {t('lessons.modals.incomplete.message_part2')}
        </div>
      )
      return {
        show: true,
        title: t('lessons.modals.incomplete.title'),
        message,
        icon: faLightbulb,
        iconType: 'info',
        buttonMessage: t('lessons.modals.buttons.accept'),
        onContinue: () => setModalData({ show: false }),
        onClose: () => setModalData({ show: false }),
      }
    },
    [t]
  )

  const clearNormativeAccessError = useCallback(() => {
    dispatch(fromLessonActions.Actions.lessonsClearNormativeAccessError())
  }, [dispatch])

  useEffect(() => {
    if (!normativeAccessReason) return

    setSubmit(false)
    setShowFinishModal(false)
  }, [normativeAccessReason])

  useEffect(() => {
    if (!nextLessonRejection) return

    setSubmit(false)
    setShowFinishModal(false)
    const rejectedLesson = course.lessons.find(
      ({ id }) => id === nextLessonRejection.lessonId
    )
    if (rejectedLesson && rejectedLesson.id !== currentLesson.id)
      setCurrentLesson({ ...rejectedLesson })
    showIncompleteEvaluationsModal()
    dispatch(fromLessonActions.Actions.lessonsClearNextLessonRejection())
  }, [
    course.lessons,
    currentLesson.id,
    dispatch,
    nextLessonRejection,
    showIncompleteEvaluationsModal,
  ])

  const normativeAccessVariant =
    normativeAccessReason === 'missing_attestation'
      ? ('missingAttestation' as const)
      : normativeAccessReason === 'incomplete_profile'
      ? ('incompleteProfile' as const)
      : null

  const handleCloseNormativeAccessModal = useCallback(() => {
    history.push(`/courses/${course.id}/`)
    clearNormativeAccessError()
  }, [clearNormativeAccessError, course.id, history])

  const nextLesson = useCallback(() => {
    const lessons = course.lessons.map((row) => ({ ...row }))
    const currentLessonIndex = lessons.findIndex(
      ({ id }) => currentLesson.id === id
    )
    const followingLesson = lessons[currentLessonIndex + 1]
    setCurrentLesson(followingLesson)
    if (followingLesson)
      dispatch({
        type: fromLessonActions.LESSONS_GET_LESSON,
        payload: followingLesson.id,
      })
    setSubmit(false)
    if (!course.enrol || canAnswer) {
      dispatch({
        type: fromLessonActions.LESSONS_GET_NEXT_LESSON,
        payload: { lessonId: currentLesson.id, courseId: course.id },
      })
    }
  }, [
    canAnswer,
    course.enrol,
    course.id,
    course.lessons,
    currentLesson.id,
    dispatch,
  ])

  const logLessonFinished = useCallback(() => {
    const isExternal =
      course.synchronousCourse?.synchronousCourseType ===
      SynchronousCourseTypes.external

    logEvent(AmplitudeEvents.lmsLessonFinished, {
      source: JSON.stringify(origin) !== '{}' ? 'widget' : 'module',
      'Course ID': course.id,
      'Type of Platform': '',
      'Requires Application': course.mandatory,
      Sincronicity: isExternal,
      'Lesson ID': currentLesson.id,
      'Number of lesson finished': course.lessons.length,
      Section: section,
    })
  }, [course, currentLesson.id, logEvent, origin, section])

  const uncompletedScorms = () => {
    const banners = document.querySelectorAll('#block-banner')
    const filteredBanners = Array.from(banners).filter((banner) => {
      return banner.getAttribute('status') !== '2'
    })
    return filteredBanners.length > 0
  }

  const participantCanProceedLesson = useCallback(() => {
    const emptyMandatories = lessonValidatorByRef(mandatoriesRef)
    return (
      (!emptyMandatories.length &&
        everyHasResponse &&
        multimediaProgress.finished &&
        !uncompletedScorms()) ||
      !canAnswer
    )
  }, [everyHasResponse, multimediaProgress.finished, canAnswer])

  const handleGetNextLesson = useCallback(() => {
    setSubmit(true)
    logLessonFinished()

    const lessons = course.lessons.map((row) => ({ ...row }))
    const { Finished } = AssignedLessonStateEnum
    const currentLessonIndex = lessons.findIndex(
      ({ id }) => currentLesson.id === id
    )
    const isFinishAction = currentLessonIndex === lessons.length - 1

    if (canAnswer && mandatoryTaskUploadInProgress) {
      setPendingActivities(true)
      return
    }

    if (canAnswer && loading) {
      setPendingActivities(true)
      return
    }

    if (participantCanProceedLesson()) {
      // Hay menos assignedLessons que lecciones en el curso
      if (isFinishAction) {
        // Cuantas lecciones no finalizadas más las que no existen en assignedLessons hay (sin contar la actual)
        const unfinishedCount =
          lessons.filter(({ id }) => id !== currentLesson.id).length -
          courseProgress.filter(
            ({ state, lessonId }) =>
              state === Finished && lessonId !== currentLesson.id
          ).length
        if (!course.enrol || canAnswer) {
          unfinishedCount > 0
            ? setModalData(prepareModalData(unfinishedCount))
            : setShowFinishModal(true)
        } else {
          history.push('/')
        }
      } else nextLesson()
    } else {
      if (canAnswer && !everyTasksHasResponse) {
        showIncompleteTasksModal()
      }
      setPendingActivities(true)
    }
  }, [
    course.enrol,
    course.lessons,
    courseProgress,
    currentLesson.id,
    everyHasResponse,
    everyTasksHasResponse,
    canAnswer,
    loading,
    mandatoryTaskUploadInProgress,
    history,
    logLessonFinished,
    multimediaProgress.finished,
    nextLesson,
    participantCanProceedLesson,
    prepareModalData,
    showIncompleteTasksModal,
  ])

  const handleGoToLesson = (lesson: ILessonModel) => {
    setCurrentLesson(lesson)
    dispatch({ type: fromLessonActions.LESSONS_GET_LESSON, payload: lesson.id })
  }

  const handleFinish = useCallback(() => {
    if (canAnswer && mandatoryTaskUploadInProgress) {
      setShowFinishModal(false)
      setPendingActivities(true)
      return
    }

    if (canAnswer && loading) {
      setShowFinishModal(false)
      setPendingActivities(true)
      return
    }

    if (!participantCanProceedLesson()) {
      setShowFinishModal(false)
      if (canAnswer && !everyTasksHasResponse) {
        showIncompleteTasksModal()
      }
      setPendingActivities(true)
      return
    }

    dispatch({
      type: fromLessonActions.LESSONS_GET_NEXT_LESSON,
      payload: {
        lessonId: currentLesson.id,
        courseId: course.id,
        finishCourse: true,
      },
    })
  }, [
    canAnswer,
    course.id,
    currentLesson.id,
    dispatch,
    everyTasksHasResponse,
    loading,
    mandatoryTaskUploadInProgress,
    participantCanProceedLesson,
    setPendingActivities,
    setShowFinishModal,
    showIncompleteTasksModal,
  ])

  const handleSendMessage = (data: IChatMessageModel) => {
    dispatch({
      type: fromChatActions.CREATE_CHAT_MESSAGE,
      payload: { ...data, identifier },
    })
  }

  return {
    submit,
    setSubmit,
    modalData,
    setModalData,
    currentLesson,
    setCurrentLesson,
    showFinishModal,
    setShowFinishModal,
    pendingActivities,
    setPendingActivities,
    handleGetPrevLesson,
    handleGetNextLesson,
    handleGoToLesson,
    handleFinish,
    handleSendMessage,
    showIncompleteMultimediaModal,
    showIncompleteEvaluationsModal,
    showIncompleteTasksModal,
    normativeAccessVariant,
    handleCloseNormativeAccessModal,
    setMandatoriesRef,
    validations: {
      everyHasResponse,
      everyEvaluationsHasResponse,
      everyTasksHasResponse,
    },
  }
}

export default useActions
